import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { pb } from '../lib/pb';
import { formatDate } from '../i18n';
import type { HistoryRecord } from '../lib/types';
import { isEditor, useStore } from '../store/store';
import { CommentThread } from '../panels/inspector/CommentsTab';
import { setData, useData } from './dataStore';
import { refKeyOf, useReverseRefs } from './hooks';
import { DialoguePreview, isDialogue } from './DialoguePreview';
import { DataIcon } from './DataIcon';
import { categoryIcon } from './categories';
import { FieldList, Value } from './FieldView';
import { RecordEditor } from './RecordEditor';
import { StatusActions } from './StatusActions';
import { allFields, classChain, fieldLabel, settingKey } from './schema';
import { same } from './values';
import type { DataRecord } from './types';

type Tab = 'properties' | 'comments' | 'usedBy' | 'unity' | 'history';

export function RecordView({ record: r }: { record: DataRecord }) {
  const { t } = useTranslation();
  const classes = useData((s) => s.classes);
  const categories = useData((s) => s.categories);
  const usedBy = useReverseRefs()[refKeyOf(r)] ?? [];
  const [previewing, setPreviewing] = useState(false);
  const [tab, setTab] = useState<Tab>('properties');
  const cat = categories.find((c) => c.key === r.category);
  const cls = classes[r.className];
  const canEdit = isEditor(useStore((s) => s.user)) && !!cat?.editable && r.status !== 'deprecated';

  return (
    <article className="record">
      <button className="link small back-link" onClick={() => setData({ selectedId: null })}>
        ←{' '}
        {t('data.table.back', {
          name: t(`data.categories.${r.category}`, { defaultValue: cat?.label ?? r.category }),
        })}
      </button>
      <header className="record-head">
        <DataIcon iconKey={r.icon} size={48} fallback={categoryIcon(r.category)} />
        <div className="insp-title">
          <div className="name">{r.name}</div>
          <div className="muted small">
            {t(`data.categories.${r.category}`, { defaultValue: cat?.label ?? r.category })}
            {cls && cls.className !== cat?.baseClass && <> · {cls.label}</>}
            {r.key !== r.name && (
              <>
                {' · '}
                <span className="mono">{r.key}</span>
              </>
            )}
          </div>
        </div>
        <span className={`status-pill ${r.status}`}>{t(`status.${r.status}`)}</span>
        {r.syncState && r.syncState !== 'in_sync' && (
          <span className={`sync-pill ${r.syncState}`}>{t(`sync.${r.syncState}`)}</span>
        )}
      </header>
      <StatusActions record={r} />
      {r.syncState === 'missing_in_unity' && <div className="banner warn">{t('data.missingBanner')}</div>}
      {!!r.conflictFields?.length && (
        <div className="banner warn">{t('data.conflictBanner', { fields: r.conflictFields.join(', ') })}</div>
      )}
      <nav className="tabs">
        {(['properties', 'comments', 'usedBy', 'unity', 'history'] as Tab[]).map((k) => (
          <button key={k} className={tab === k ? 'active' : ''} onClick={() => setTab(k)}>
            {t(`data.tabs.${k}`)}
            {k === 'usedBy' && usedBy.length > 0 && <span className="count"> {usedBy.length}</span>}
          </button>
        ))}
      </nav>
      <div className="record-body">
        {tab === 'properties' &&
          (canEdit ? (
            <RecordEditor key={r.id} record={r} />
          ) : (
            <>
              {!cat?.editable && <p className="muted small">{t('data.edit.readOnlyCategory')}</p>}
              {r.needsNiklas && (
                <div className="banner">
                  <strong>{t('data.edit.note')}</strong>
                  <span>{r.needsNiklas}</span>
                </div>
              )}
              {isDialogue(classChain(classes, r.className)) && (
                <button className="small" onClick={() => setPreviewing(true)}>
                  ▶ {t('data.dialogue.preview')}
                </button>
              )}
              <FieldList className={r.className} values={r.values} root={r.values} />
              {previewing && <DialoguePreview values={r.values ?? {}} onClose={() => setPreviewing(false)} />}
            </>
          ))}
        {tab === 'comments' && <CommentThread target={{ field: 'dataRecord', id: r.id }} />}
        {tab === 'usedBy' && <UsedBy refs={usedBy} />}
        {tab === 'unity' && <UnityInfo record={r} chain={classChain(classes, r.className)} />}
        {tab === 'history' && <DataHistory record={r} />}
      </div>
    </article>
  );
}

function UsedBy({ refs }: { refs: { id: string; field: string }[] }) {
  const { t, i18n } = useTranslation();
  const records = useData((s) => s.records);
  const classes = useData((s) => s.classes);
  const settings = useData((s) => s.settings);
  if (refs.length === 0) return <p className="muted">{t('data.usedByNone')}</p>;
  return (
    <ul className="rec-list">
      {refs.map(({ id, field }) => {
        const r = records[id];
        if (!r) return null;
        const f = allFields(classes, r.className).find((x) => x.name === field);
        const label = f ? fieldLabel(f, settings[settingKey(f.owner, f.name)], i18n.language) : field;
        return (
          <li key={id + field}>
            <button className="rec" onClick={() => setData({ selectedId: r.id, category: r.category })}>
              <DataIcon iconKey={r.icon} fallback={categoryIcon(r.category)} />
              <span className="rec-text">
                <span className="ellipsis">{r.name}</span>
                <span className="muted small">
                  {t(`data.categories.${r.category}`, { defaultValue: r.category })} · {label}
                </span>
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function UnityInfo({ record: r, chain }: { record: DataRecord; chain: string[] }) {
  const { t, i18n } = useTranslation();
  const classes = useData((s) => s.classes);
  const settings = useData((s) => s.settings);
  const lastImport = useData((s) => s.lastImport);
  const diffs = allFields(classes, r.className).filter(
    (f) => !same(r.values?.[f.name], r.unityValues?.[f.name]),
  );
  return (
    <div className="unity">
      <dl className="stats">
        <div className="stat-pair">
          <dt>{t('data.unity.path')}</dt>
          <dd className="mono small">{r.assetPath || r.pendingPath || '—'}</dd>
        </div>
        <div className="stat-pair">
          <dt>{t('data.unity.class')}</dt>
          <dd className="mono small">{chain.join(' → ')}</dd>
        </div>
        <div className="stat-pair">
          <dt>GUID</dt>
          <dd className="mono small">{r.guid || '—'}</dd>
        </div>
        <div className="stat-pair">
          <dt>{t('data.unity.sync')}</dt>
          <dd>{r.syncState ? t(`sync.${r.syncState}`) : '—'}</dd>
        </div>
        {lastImport && (
          <div className="stat-pair">
            <dt>{t('data.unity.lastImport')}</dt>
            <dd className="small">
              {formatDate(lastImport.created)}
              {lastImport.unityGitCommit && (
                <span className="mono muted"> · {lastImport.unityGitCommit.slice(0, 8)}</span>
              )}
            </dd>
          </div>
        )}
      </dl>
      {diffs.length > 0 && (
        <>
          <h4 className="group-label">{t('data.unity.differs')}</h4>
          <table className="diff small">
            <thead>
              <tr>
                <th />
                <th>{t('data.unity.design')}</th>
                <th>Unity</th>
              </tr>
            </thead>
            <tbody>
              {diffs.map((f) => (
                <tr key={f.name} className={r.conflictFields?.includes(f.name) ? 'changed' : ''}>
                  <td>{fieldLabel(f, settings[settingKey(f.owner, f.name)], i18n.language)}</td>
                  <td>
                    <Value field={f} value={r.values?.[f.name]} />
                  </td>
                  <td>
                    <Value field={f} value={r.unityValues?.[f.name]} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

function DataHistory({ record: r }: { record: DataRecord }) {
  const { t } = useTranslation();
  const users = useStore((s) => s.users);
  const [rows, setRows] = useState<HistoryRecord[]>([]);
  useEffect(() => {
    let cancelled = false;
    void pb
      .collection('history')
      .getList<HistoryRecord>(1, 100, {
        filter: pb.filter('targetCollection = "data_records" && recordId = {:r}', { r: r.id }),
        sort: '-created',
      })
      .then((res) => !cancelled && setRows(res.items));
    return () => {
      cancelled = true;
    };
  }, [r.id, r.rev]);
  if (rows.length === 0) return <p className="muted">{t('history.empty')}</p>;
  return (
    <ol className="history">
      {rows.map((h) => (
        <li key={h.id}>
          <div className="small">
            <span className={`hist-action ${h.action}`}>{t(`history.actions.${h.action}`)}</span>{' '}
            <strong>{users[h.actor]?.name ?? t('history.system')}</strong>{' '}
            <span className="muted">{formatDate(h.updated)}</span>
          </div>
          {h.summary && <div className="muted small">{h.summary}</div>}
        </li>
      ))}
    </ol>
  );
}
